import { useTranslation } from 'react-i18next';
import { IconProps, DateIcon } from '@hero/web-icons';
import { OrderFromToDate } from '@hero/schema';
import { getOrderDisplayFromTo } from '@hero/core';
import { cn } from '@hero/web-lib';

interface PickupDateDisplayProps {
  pickupDate: OrderFromToDate | undefined | null;
  /**
   * - `button` - will display as a clickable button card (80px height)
   * - `one_line_text` - will display the date and time in one line
   * - `two_line_text` - will display the date and time in two lines
   */
  variant: 'button' | 'one_line_text' | 'two_line_text';

  iconProps?: IconProps;
  className?: string;
  /** on click open the pickup date modal if you want*/
  onClick?: () => void;
}

/**
 * PickupDateDisplay - Reusable component for displaying pickup date & time
 *
 * Provides consistent date/time formatting across the app with multiple display variants.
 * Uses getOrderDisplayFromTo for consistent formatting.
 *
 * @example
 * ```tsx
 * // Button variant (clickable card)
 * <PickupDateDisplay
 *   variant="button"
 *   pickupDate={{ from: '2024-01-15T18:00:00Z', to: '2024-01-15T20:00:00Z' }}
 *   onClick={() => setModalOpen(true)}
 * />
 *
 * // One line text
 * <PickupDateDisplay
 *   variant="one_line_text"
 *   pickupDate={{ from: '2024-01-15T18:00:00Z', to: '2024-01-15T20:00:00Z' }}
 * />
 *
 * // Two line text
 * <PickupDateDisplay
 *   variant="two_line_text"
 *   pickupDate={{ from: '2024-01-15T18:00:00Z', to: '2024-01-15T20:00:00Z' }}
 * />
 * ```
 */
export function PickupDateDisplay({
  pickupDate,
  variant,
  iconProps,
  className,
  onClick,
}: PickupDateDisplayProps) {
  const { t, i18n } = useTranslation(['m-order', 'c-dates']);

  const translateDate = (key: string, fallback?: string): string => {
    const keyMap: Record<string, string> = {
      'date.today': 'c-dates:relative_dates.today',
      'date.tomorrow': 'c-dates:relative_dates.tomorrow',
    };
    return t(keyMap[key] || key, { defaultValue: fallback || key });
  };

  // Get formatted date/time display
  const getDisplay = () => {
    if (!pickupDate) {
      return {
        dateStr: t('m-order:pickup_date.title'),
        timeStr: t('m-order:pickup_date.description'),
        isEmpty: true,
      };
    }

    const display = getOrderDisplayFromTo({
      from: pickupDate.from,
      to: pickupDate.to,
      translate: translateDate,
      isRTL: i18n.dir() === 'rtl',
    });

    return {
      dateStr: display.dateAutoOnly,
      timeStr: display.timeOnly,
      isEmpty: false,
    };
  };

  const { dateStr, timeStr, isEmpty } = getDisplay();

  // Button variant - clickable card
  if (variant === 'button') {
    const Component = onClick ? 'button' : 'div';
    return (
      <Component
        onClick={onClick}
        className={cn(
          'flex items-center p-3 w-full rounded-lg border h-[80px]',
          onClick && 'cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors',
          'border-gray-300',
          className,
        )}>
        <div className="flex gap-2 items-center flex-1 min-w-0 h-full">
          <DateIcon variant={isEmpty ? 'secondary' : 'primary'} className="size-5" {...iconProps} />

          <div
            className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
            <h3
              className={cn(
                'text-[14px] font-[600] leading-[20px] truncate',
                isEmpty ? 'text-gray-500' : 'text-gray-900',
              )}>
              {dateStr}
            </h3>
            <p className="text-[12px] font-[500] leading-[18px] text-gray-500 line-clamp-1">
              {timeStr}
            </p>
          </div>
        </div>
      </Component>
    );
  }

  // One line text variant
  if (variant === 'one_line_text') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <DateIcon size="small" {...iconProps} />
        <span className="text-[14px] text-gray-900">
          {isEmpty ? dateStr : `${dateStr} ${timeStr}`}
        </span>
      </div>
    );
  }

  // Two line text variant
  if (variant === 'two_line_text') {
    return (
      <div className={cn('flex items-start gap-2', className)}>
        <DateIcon size="small" className="mt-0.5" {...iconProps} />
        <div className="flex flex-col text-start">
          <span className="text-[14px] font-[600] text-gray-900">{dateStr}</span>
          <span className="text-[12px] text-gray-500">{timeStr}</span>
        </div>
      </div>
    );
  }

  return null;
}
